import { InputAdornment } from "@mui/material";
import { useFieldAccessory } from "components/Forms/FieldAccessory";
import { useSuggestedValue } from "hooks/useSuggestedValue";
import { useMemo } from "react";
import {
  Control,
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
} from "react-hook-form";

import { SuggestedIcon } from "./SuggestedIcon";

type SuggestedFieldRenderProps<T extends FieldValues> = {
  field: ControllerRenderProps<T, Path<T>>;
  fieldState: ControllerFieldState;
  isSuggested: boolean;
  /** InputAdornment with SparkleIcon, for use in TextField slotProps */
  sparkleAdornment: React.ReactElement;
  /** Standalone SparkleIcon, for use next to checkboxes or custom controls */
  sparkleIcon: React.ReactElement;
  /**
   * What the surrounding form wants rendered under this field, if anything.
   *
   * Null unless a `FieldAccessoryProvider` is above this control. On a TextField
   * pass it through `fieldAccessorySlot`; on a custom control put it wherever
   * "under the input" means for that control.
   */
  accessory: React.ReactNode;
};

type SuggestedFieldProps<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  needsReview: boolean;
  /**
   * The field's visible label, forwarded to a `FieldAccessoryProvider` so it can
   * name the field. Only the call site knows it; nothing here renders it.
   */
  label?: React.ReactNode;
  children: (props: SuggestedFieldRenderProps<T>) => React.ReactElement;
};

/**
 * A render-prop wrapper around Controller that computes suggestion state
 * and provides a sparkle adornment. Use for any field type that needs
 * AI suggestion indicators (checkboxes, custom numeric inputs, etc).
 *
 * For plain TextFields, use SuggestedTextField instead.
 *
 * Render `accessory` wherever "under the input" means for your control, or the
 * field silently loses anything a `FieldAccessoryProvider` above it wants to put
 * there — on the review surfaces, the row of values it could be filled with.
 * `fieldAccessorySlot` places it for a `TextField`.
 *
 * @example
 * <SuggestedField name="containerNumber" control={control} needsReview={needsReview}>
 *   {({ field, isSuggested, sparkleAdornment, accessory }) => {
 *     const slot = fieldAccessorySlot(accessory, undefined);
 *     return (
 *       <TextField
 *         value={field.value ?? ""}
 *         onChange={(e) => field.onChange(safeParseInt(e.target.value))}
 *         helperText={slot.helperText}
 *         slotProps={{
 *           formHelperText: slot.formHelperText,
 *           input: { startAdornment: isSuggested ? sparkleAdornment : undefined },
 *         }}
 *       />
 *     );
 *   }}
 * </SuggestedField>
 */
export function SuggestedField<T extends FieldValues>({
  name,
  control,
  needsReview,
  label,
  children,
}: SuggestedFieldProps<T>) {
  const isSuggestedValue = useSuggestedValue(needsReview);
  const accessory = useFieldAccessory(name, label);

  const sparkleIcon = useMemo(() => <SuggestedIcon isSuggested size="small" />, []);

  const sparkleAdornment = useMemo(
    () => <InputAdornment position="start">{sparkleIcon}</InputAdornment>,
    [sparkleIcon]
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) =>
        children({
          field,
          fieldState,
          isSuggested: isSuggestedValue(field.value, fieldState),
          sparkleAdornment,
          sparkleIcon,
          accessory,
        })
      }
    />
  );
}
